import React, { useEffect, useRef, useState } from 'react';
import { Alert, Modal, Spin, Typography, Space } from 'antd';
import { ThunderboltOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import logger from "../../../services/logger";

/**
 * Plugin form builder « Contenu IA ».
 *
 * Bloc d'affichage pur (ne collecte AUCUNE valeur du formulaire) : il insère un
 * bloc de contenu HTML généré par l'IA (collection `ai_content_blocks`),
 * identifié par un simple uuid stocké dans `data.blockId`.
 *
 * ⚠️ AGNOSTIQUE PAR DESIGN (déploiement via `copyFormPlugins.bash` qui fait un
 * `cp -r` du dossier formPlugins/ admin → web). Ce fichier est copié TEL QUEL
 * vers `web/components/WebEditor/formPlugins/aiContent.js` et doit compiler des
 * deux côtés. Il ne dépend donc QUE de react / antd / @ant-design/icons /
 * @lingui et de l'instance `formPlugin` (qui expose `BASE_URL`). Aucun import
 * vers `src/components/custom-fields/` (admin-only) ni `AiContentSlot` (web-only).
 *
 * - Renderer : aperçu live du HTML du bloc (iframe sandbox + auto-resize),
 *   identique côté admin (édition) et côté web (formulaire public en live).
 * - Controls (panneau de config, affiché uniquement côté admin) : boutons
 *   Créer / Modifier / Supprimer. « Créer » génère un uuid + ouvre l'éditeur IA
 *   (`/editor/ai/content?id=...`) dans un nouvel onglet.
 */
export default (formPlugin, namespace) => {
    return {
        Renderer: (props) => {
            if (props.remove) {
                formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id);
            }

            const { data } = props;
            const blockId = data.blockId;

            if (!blockId) {
                if (!props.isPreviewMode && !props.readOnly) {
                    return (
                        <Alert
                            showIcon
                            type="warning"
                            message={t`Aucun contenu IA configuré. Sélectionnez le bloc puis cliquez sur « Créer un contenu IA ».`}
                        />
                    );
                }
                return null;
            }

            return (
                <AiBlockPreview
                    native={!!formPlugin.runtime}
                    baseUrl={formPlugin.BASE_URL}
                    blockId={blockId}
                    lang={props.lang}
                />
            );
        },
        namespace: namespace,
        id: 'form-ai-content' + namespace,
        title: t`Contenu IA`,
        description: t`Bloc de contenu généré par l'IA.`,
        icon: <ThunderboltOutlined />,
        iconColor: "#6366f1",
        version: 1,
        controls: {
            type: 'custom',
            Component: (props) => (
                <AiBlockControls baseUrl={formPlugin.BASE_URL} {...props} />
            ),
        },
    };
};

/* ────────────────────────────────────────────────────────────────────────────
 * Panneau de config (admin uniquement — non monté en mode live côté web).
 * Boutons Créer / Modifier / Supprimer, inline (pas de dépendance externe).
 * ──────────────────────────────────────────────────────────────────────────── */
function AiBlockControls({ data, onChange, baseUrl }) {
    const [deleting, setDeleting] = useState(false);
    const blockId = data?.blockId || null;

    const openEditor = (id) => {
        if (!id || typeof window === "undefined") return;
        // EDITOR_PREFIX_PATH = '/editor' (cf. admin AppConfig). Hardcodé pour
        // rester agnostique — ce bouton n'est cliqué que dans l'éditeur admin.
        window.open(`/editor/ai/content?id=${id}`, "_blank");
    };

    const genUuid = () => {
        if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
        return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    };

    const handleCreate = () => {
        const id = genUuid();
        openEditor(id);
        onChange({ blockId: id });
    };

    const handleEdit = () => {
        if (!blockId) return;
        openEditor(blockId);
    };

    const handleDelete = () => {
        if (!blockId) return;
        Modal.confirm({
            title: t`Supprimer le contenu IA ?`,
            content: t`Cette action est définitive. Le bloc de contenu généré par l'IA sera supprimé.`,
            okText: t`Supprimer`,
            okType: "danger",
            cancelText: t`Annuler`,
            onOk: async () => {
                setDeleting(true);
                try {
                    await fetch(`${baseUrl}/ai-content-blocks/delete/${blockId}`);
                } catch (e) {
                    logger.error({ err: e }, "[aiContent] delete failed:");
                } finally {
                    setDeleting(false);
                    onChange({ blockId: null });
                }
            },
        });
    };

    if (!blockId) {
        return (
            <div style={{
                padding: 16,
                border: "1.5px dashed #d6d6d6",
                borderRadius: 10,
                background: "#fafafa",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
            }}>
                <ThunderboltOutlined style={{ fontSize: 22, color: "#6366f1" }} />
                <Typography.Text type="secondary" style={{ textAlign: "center", fontSize: 13 }}>
                    <Trans>Aucun contenu personnalisé. Créez un bloc avec l'aide de l'IA.</Trans>
                </Typography.Text>
                <button
                    type="button"
                    onClick={handleCreate}
                    style={primaryBtnStyle}
                >
                    <ThunderboltOutlined /> <Trans>Créer un contenu IA</Trans>
                </button>
            </div>
        );
    }

    return (
        <div style={{
            padding: 14,
            border: "1px solid #e5e5e5",
            borderRadius: 10,
            background: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: 12,
        }}>
            <Space size={8}>
                <ThunderboltOutlined style={{ fontSize: 16, color: "#6366f1" }} />
                <Typography.Text strong><Trans>Contenu IA personnalisé</Trans></Typography.Text>
            </Space>
            <Typography.Text type="secondary" style={{ fontSize: 11 }} ellipsis>
                ID : {blockId}
            </Typography.Text>
            <Space size={6}>
                <button type="button" onClick={handleEdit} style={lightBtnStyle}>
                    <EditOutlined /> <Trans>Modifier</Trans>
                </button>
                <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    style={dangerBtnStyle}
                >
                    <DeleteOutlined />
                </button>
            </Space>
        </div>
    );
}

const primaryBtnStyle = {
    cursor: "pointer",
    border: "none",
    borderRadius: 8,
    padding: "6px 14px",
    background: "#6366f1",
    color: "#fff",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
};
const lightBtnStyle = {
    cursor: "pointer",
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    padding: "5px 12px",
    background: "#f5f5f5",
    color: "#1f2128",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
};
const dangerBtnStyle = {
    cursor: "pointer",
    border: "1px solid #ffd6d6",
    borderRadius: 8,
    padding: "5px 12px",
    background: "#fff1f1",
    color: "#FF3131",
    display: "inline-flex",
    alignItems: "center",
};

/* ────────────────────────────────────────────────────────────────────────────
 * Aperçu inline du bloc IA : fetch via baseUrl + iframe sandbox auto-resize.
 * Identique admin / web (pas de dépendance à AiHtmlIframe ni AiContentSlot).
 * ──────────────────────────────────────────────────────────────────────────── */
function AiBlockPreview({ baseUrl, blockId, lang, native }) {
    const iframeRef = useRef(null);
    const [block, setBlock] = useState(null);
    const [loading, setLoading] = useState(false);
    const [height, setHeight] = useState(0);
    // ID unique et STABLE par instance : sans lui, plusieurs aperçus sur la
    // même page reçoivent tous le même message global et appliquent la hauteur
    // d'une AUTRE iframe → hauteur fausse une fois sur deux. Le srcdoc renvoie
    // cet id, le handler ne garde que les messages de SON iframe.
    const frameIdRef = useRef(null);
    if (frameIdRef.current === null) {
        frameIdRef.current = "aicbp_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    }

    const langCode = lang && typeof lang === "object" ? lang.value : lang;

    // Charge le bloc (auto-create côté API si l'id n'existe pas encore).
    // Race-safe : ignore les réponses obsolètes si blockId change vite.
    useEffect(() => {
        if (!blockId) {
            setBlock(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const req = await fetch(`${baseUrl}/ai-content-blocks/id/${encodeURIComponent(blockId)}`);
                if (cancelled) return;
                const json = await req.json();
                if (cancelled) return;
                setBlock(json?.data || null);
            } catch (e) {
                if (cancelled) return;
                logger.error({ err: e }, "[aiContent] preview fetch error:");
                setBlock(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [baseUrl, blockId]);

    // Injecte le HTML (traduit) dans l'iframe avec script d'auto-resize.
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe || !block?.html) return;

        const mainLang = block.mainLang || "fr";
        const targetLang = langCode || mainLang;
        const dir = getBlockDirection(targetLang);
        const html = translateHtml(block.html, block.i18n, mainLang, targetLang);
        const finalHtml = stripViewportUnits(html);

        const childScript = `
<script>
  (function() {
    var lastHeight = 0, rafId = null;
    // Mesure robuste : capture les marges (margin collapsing) que
    // body.scrollHeight ignore, via getBoundingClientRect du dernier enfant.
    function measure() {
      var de = document.documentElement, body = document.body;
      var h = Math.max(body.scrollHeight, body.offsetHeight, de.scrollHeight, de.offsetHeight);
      var last = body.lastElementChild;
      if (last) {
        var rect = last.getBoundingClientRect();
        var mb = parseFloat(getComputedStyle(last).marginBottom) || 0;
        h = Math.max(h, Math.ceil(rect.bottom + mb));
      }
      return h;
    }
    var FRAME_ID = ${JSON.stringify(frameIdRef.current)};
    function resize() {
      var h = measure();
      if (Math.abs(h - lastHeight) < 2) return;
      lastHeight = h;
      parent.postMessage({ __aiBlockPreviewHeight: true, id: FRAME_ID, height: h }, '*');
    }
    // rAF (debounce quand peint) + setTimeout de secours (rAF est gelé
    // quand l'iframe n'est pas visible / onglet en arrière-plan).
    var toId = null;
    function scheduleResize() {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(resize);
      if (toId) clearTimeout(toId);
      toId = setTimeout(resize, 0);
    }
    window.addEventListener('load', scheduleResize);
    window.addEventListener('resize', scheduleResize);
    // Observe <html> ET <body> ; MutationObserver pour les injections DOM
    // tardives (lucide.createIcons, contenu différé…).
    var observer = new ResizeObserver(scheduleResize);
    observer.observe(document.documentElement);
    observer.observe(document.body);
    var mo = new MutationObserver(scheduleResize);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleResize);
    function watchMedia() {
      var medias = document.querySelectorAll('img, iframe, video');
      for (var i = 0; i < medias.length; i++) {
        var m = medias[i];
        if (m.complete) continue;
        m.addEventListener('load', scheduleResize, { once: true });
        m.addEventListener('error', scheduleResize, { once: true });
      }
    }
    watchMedia();
    setTimeout(scheduleResize, 100);
    setTimeout(scheduleResize, 500);
    setTimeout(function(){ watchMedia(); scheduleResize(); }, 1200);
    setTimeout(scheduleResize, 2500);
    document.addEventListener('click', function(e) {
      var a = e.target.closest('a');
      if (a && a.getAttribute('href') && !a.target) a.target = '_blank';
    }, true);
    scheduleResize();
  })();
<\/script>`;

        iframe.srcdoc = `<!DOCTYPE html>
<html lang="${targetLang}" dir="${dir}">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<base target="_blank">
<style>
  html, body { margin: 0; padding: 0; background: transparent; overflow: hidden; }
  html, body { height: auto !important; min-height: 0 !important; }
  /* Suit la direction du document (rtl pour l'hébreu) sans forcer une valeur. */
  body { text-align: start; }
</style>
</head>
<body>
<div dir="${dir}">
${finalHtml}
</div>
${childScript}
</body>
</html>`;
    }, [block, langCode]);

    // Écoute les messages de redimensionnement.
    useEffect(() => {
        const handler = (e) => {
            if (
                e.data &&
                e.data.__aiBlockPreviewHeight &&
                e.data.id === frameIdRef.current &&   // n'accepte que SON iframe
                typeof e.data.height === "number"
            ) {
                setHeight(e.data.height);
            }
        };
        if (typeof window !== "undefined") window.addEventListener("message", handler);
        return () => {
            if (typeof window !== "undefined") window.removeEventListener("message", handler);
        };
    }, []);

    if (!blockId) return null;
    if (loading && !block) return <Spin spinning style={{ display: "block", padding: 24 }} />;
    if (!block?.html) return null;

    return (
        <div style={{ width: "100%" }}>
            <iframe
                ref={iframeRef}
                title="ai-content-block-preview"
                scrolling="no"
                sandbox={native ? "allow-scripts allow-popups" : "allow-scripts allow-same-origin allow-popups"}
                style={{
                    width: "100%",
                    height: `${height}px`,
                    border: "none",
                    display: "block",
                    background: "transparent",
                }}
            />
        </div>
    );
}

/* ── Helpers inline (pas d'import de web/components/aiI18n — agnostique) ── */

// Traduit un fragment HTML via les [data-i18n] (et attributs data-i18n-<attr>),
// fallback langue principale. Retourne le HTML brut si cible == principale.
function translateHtml(html, i18n, mainLang, targetLang) {
    if (!html) return "";
    if (targetLang === mainLang) return html;
    const dict = i18n && i18n[targetLang];
    const fallback = i18n && i18n[mainLang];
    if (!dict && !fallback) return html;
    if (typeof DOMParser === "undefined") return html;
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div id="__root">${html}</div>`, "text/html");
        const root = doc.getElementById("__root");
        if (!root) return html;
        root.querySelectorAll("[data-i18n]").forEach((el) => {
            const key = el.getAttribute("data-i18n");
            const v = (dict && dict[key]) != null ? dict[key] : (fallback && fallback[key]);
            if (v != null) el.innerHTML = v;
        });
        ["alt", "title", "placeholder", "aria-label"].forEach((attr) => {
            root.querySelectorAll(`[data-i18n-${attr}]`).forEach((el) => {
                const key = el.getAttribute(`data-i18n-${attr}`);
                const v = (dict && dict[key]) != null ? dict[key] : (fallback && fallback[key]);
                if (v != null) el.setAttribute(attr, v);
            });
        });
        return root.innerHTML;
    } catch (e) {
        logger.warn({ err: e }, "[aiContent] translateHtml error:");
        return html;
    }
}

// Direction d'écriture du contenu DANS l'iframe. Le dir="rtl" de la page parente
// ne traverse pas la frontière de l'iframe → sans ça, un contenu hébreu reste
// rendu en LTR. Réplique inline de getLayoutDirection (web/locales) — le fichier
// est agnostique web↔admin (copié tel quel), donc pas d'import de ../locales.
// Aligné sur la source : seul l'hébreu ("he") est RTL.
const RTL_LANGS = ["he"];
function getBlockDirection(lang) {
    return RTL_LANGS.includes(lang) ? "rtl" : "ltr";
}

// Neutralise vh/vmin/vmax (boucle infinie dans iframe auto-resize).
function stripViewportUnits(html) {
    if (!html || typeof html !== "string") return html;
    return html
        .replace(/(\d+(?:\.\d+)?)vh\b/gi, (_, n) => `${Math.round(parseFloat(n) * 6.5)}px`)
        .replace(/(\d+(?:\.\d+)?)vmin\b/gi, (_, n) => `${Math.round(parseFloat(n) * 6.5)}px`)
        .replace(/(\d+(?:\.\d+)?)vmax\b/gi, (_, n) => `${Math.round(parseFloat(n) * 6.5)}px`);
}
